import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchBlogPosts, BlogPost, getExcerpt } from '../../services/wordpress';
import { havenColors } from '../../theme/theme';

const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const newPosts = await fetchBlogPosts(page, 6);
      setPosts(prevPosts => page === 1 ? newPosts : [...prevPosts, ...newPosts]);
      setHasMore(newPosts.length === 6);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMorePosts = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    const newPosts = await fetchBlogPosts(nextPage, 6);
    setPosts(prevPosts => [...prevPosts, ...newPosts]);
    setHasMore(newPosts.length === 6);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAuthorName = (post: BlogPost): string => {
    return post._embedded?.author?.[0]?.name || 'HAVEN Team';
  };

  const getFeaturedImage = (post: BlogPost): string | null => {
    return post._embedded?.['wp:featuredmedia']?.[0]?.source_url || null;
  };

  return (
    <Box sx={{ py: 8, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ 
            fontWeight: 'bold',
            color: havenColors.darkGray,
            mb: 3,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}>
            HAVEN Blog & News
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Stay up to date with the latest indoor air quality insights, product updates, and industry news from the HAVEN team.
          </Typography>
        </Box>

        {loading && page === 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={50} sx={{ color: havenColors.primaryTeal }} />
          </Box>
        ) : (
          <>
            {/* Blog Posts Grid */}
            <Grid container spacing={4}>
              {posts.map((post) => (
                <Grid item xs={12} md={6} lg={4} key={post.id}>
                  <Card sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}>
                    {getFeaturedImage(post) && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={getFeaturedImage(post)!}
                        alt={post.title.rendered}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip 
                          label={formatDate(post.date)}
                          size="small"
                          sx={{ 
                            backgroundColor: havenColors.primaryTeal,
                            color: 'white',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                      
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold',
                        color: havenColors.darkGray,
                        mb: 2,
                        fontSize: '1.1rem',
                        lineHeight: 1.3
                      }}>
                        {post.title.rendered}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ 
                        color: '#666',
                        mb: 2,
                        lineHeight: 1.5
                      }}>
                        {getExcerpt(post.excerpt.rendered, 120)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Typography variant="caption" sx={{ 
                          color: '#888',
                          fontWeight: 500
                        }}>
                          By {getAuthorName(post)}
                        </Typography>
                        
                        <Button 
                          component={RouterLink}
                          to={`/blog/${post.slug}`}
                          size="small"
                          sx={{
                            color: havenColors.primaryTeal,
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: `${havenColors.primaryTeal}10`
                            }
                          }}
                        >
                          Read More →
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Load More Button */}
            {hasMore && posts.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={loadMorePosts}
                  sx={{
                    borderColor: havenColors.primaryTeal,
                    color: havenColors.primaryTeal,
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: havenColors.primaryTeal,
                      backgroundColor: havenColors.primaryTeal,
                      color: 'white',
                    },
                  }}
                >
                  Load More Posts
                </Button>
              </Box>
            )}

            {posts.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: '#666' }}>
                  No blog posts found.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default BlogPage; 